import styled, { css, keyframes } from 'styled-components'
import { glassSurface } from './glassSurface'

// The frame of the screen, shared with the lazy wrapper's fallback (no three here): while the chunk loads, the same
// dotted backdrop and caption already stand where the scene and its loading hangar will come in.

/** Dot grid of the backdrop. */
const DOT = { radius: '1px', spacing: '22px' }

export const Section = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    padding: 0.5em;
    overflow: hidden;
`

/* The scene fills the shell; behind the transparent canvas — an editor-like dot grid over a soft backdrop (the fog
   fades into bgLayout). The dots stay put on screen whatever the camera does. */
export const Shell = styled.div<{ $isExpanded: boolean }>`
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.lg};
    box-shadow: ${({ theme }) => theme.shadows.card};
    background:
        radial-gradient(circle, ${({ theme }) => theme.colors.inputBorder} ${DOT.radius}, transparent calc(${DOT.radius} + 0.5px))
            center / ${DOT.spacing} ${DOT.spacing},
        radial-gradient(120% 90% at 50% 0%, ${({ theme }) => theme.colors.surface} 0%, transparent 60%),
        linear-gradient(180deg, ${({ theme }) => theme.colors.bgLayout} 0%, ${({ theme }) => theme.colors.accentSoft} 140%);
    overflow: hidden;
    /* Camera and carousel gestures, not page scroll / zoom. */
    touch-action: none;

    &:fullscreen {
        border: none;
        border-radius: 0;
    }

    /* No Fullscreen API (iPhone Safari): cover the viewport instead. */
    ${({ $isExpanded }) =>
        $isExpanded &&
        css`
            position: fixed;
            inset: 0;
            z-index: 1000;
            border: none;
            border-radius: 0;
        `}
`

interface ILoadingCaptionProps {
    /** The rooms have taken over: it fades out where their controls come in. */
    isHidden: boolean
}

/** "Loading the warehouse" at the bottom, where the rooms' controls will be. */
export const LoadingCaption = ({ isHidden }: ILoadingCaptionProps) => (
    <Caption role='status' aria-hidden={isHidden} $hidden={isHidden}>
        <Breathing $hidden={isHidden}>טוען מחסן…</Breathing>
    </Caption>
)

const FADE = '250ms'

const breathe = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
`

const Caption = styled.div<{ $hidden: boolean }>`
    position: absolute;
    bottom: 0.75rem;
    left: 50%;
    z-index: 24;
    padding: 0.55rem 1rem;
    direction: rtl;
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink3};
    ${glassSurface}
    border-radius: ${({ theme }) => theme.radii.md};
    white-space: nowrap;
    pointer-events: none;
    opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
    transform: translateX(-50%) translateY(${({ $hidden }) => ($hidden ? '6px' : '0')});
    visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
    transition:
        opacity ${FADE} ease,
        transform ${FADE} ease,
        visibility 0s linear ${({ $hidden }) => ($hidden ? FADE : '0s')};

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`

/* Paused once hidden: a hidden element's animation would keep ticking for as long as the screen is open. */
const Breathing = styled.span<{ $hidden: boolean }>`
    animation: ${breathe} 1.6s ease-in-out infinite;
    animation-play-state: ${({ $hidden }) => ($hidden ? 'paused' : 'running')};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`
